/**
 * Keep the current window scroll across a brief layout/nav swap.
 *
 * Does NOT re-pin on every `scroll` event — that fights the user when they
 * start scrolling right after a tab/locale change and feels like jerking for
 * ~1s. Instead: restore a few times after the swap, and abort immediately on
 * the first intentional user scroll gesture.
 */
export function preserveWindowScroll(
  run: () => void,
  settleMs = 300
): () => void {
  const scrollY = window.scrollY

  const restore = () => {
    if (Math.abs(window.scrollY - scrollY) > 0.5) {
      window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' })
    }
  }

  let released = false
  let raf = 0
  const timers: number[] = []

  const release = () => {
    if (released) {
      return
    }
    released = true
    cancelAnimationFrame(raf)
    for (const timer of timers) {
      window.clearTimeout(timer)
    }
    window.removeEventListener('wheel', onUserIntent, { capture: true })
    window.removeEventListener('touchmove', onUserIntent, { capture: true })
    window.removeEventListener('keydown', onUserIntent, { capture: true })
  }

  const onUserIntent = () => {
    release()
  }

  window.addEventListener('wheel', onUserIntent, {
    capture: true,
    passive: true
  })
  window.addEventListener('touchmove', onUserIntent, {
    capture: true,
    passive: true
  })
  window.addEventListener('keydown', onUserIntent, {
    capture: true,
    passive: true
  })

  run()
  restore()

  raf = requestAnimationFrame(() => {
    if (released) {
      return
    }
    restore()
    raf = requestAnimationFrame(() => {
      if (!released) {
        restore()
      }
    })
  })

  for (const delay of [0, 50, settleMs]) {
    timers.push(
      window.setTimeout(() => {
        if (!released) {
          restore()
        }
      }, delay)
    )
  }

  timers.push(window.setTimeout(release, settleMs + 32))
  return release
}
